import { Component } from '../Component';
import { ArgSpecDictionary } from '../ComponentUtils';
import { HHDSImageTagName } from '../Image/Image';
import gsap from 'gsap';
import { rosAnimation, STAnimationType } from '../../utils/ScrollTriggerHelper';

const DEBUG_VERBOSE: boolean = false;
export const HHDSSplitImageSliderTagName: string = 'hhds-split-image-slider';
const TAG_NAME: string = HHDSSplitImageSliderTagName;
const LOG_PREFIX: string = `[${TAG_NAME}]`;

export const HHDSSplitImageSliderAttrNames = {
	header: 'header',
	leftSrcset: 'left-srcset',
	leftSizes: 'left-sizes',
	leftSrc: 'left-src',
	leftAlt: 'left-alt',
	leftSubtitle: 'left-subtitle',
	rightSrc: 'right-src',
	rightSrcset: 'right-srcset',
	rightSizes: 'right-sizes',
	rightAlt: 'right-alt',
	rightSubtitle: 'right-subtitle',
	rosAnimation: 'ros-animation',
};

const Attrs = HHDSSplitImageSliderAttrNames;

export class HHDSSplitImageSlider extends Component {
	private listenerFuncs: { [key: string]: any } = {};
	private leftImageContainer!: HTMLElement;
	private rightImageContainer!: HTMLElement;
	private imagesContainer!: HTMLElement;
	private componentEl!: HTMLElement;
	private divider!: HTMLElement;
	private dividerSurround!: HTMLElement;
	private dividerArrow!: HTMLElement;
	private leftSubtitle: HTMLElement | null = null;
	private rightSubtitle: HTMLElement | null = null;
	private mouseDown: boolean = false;
	private _splitPoint: number = 0.5;

	private imagesContainerTween: gsap.core.Tween | null = null;
	private tween: gsap.core.Tween | null = null;

	constructor() {
		super();

		this.listenerFuncs['left-subtitle-click'] = () => {
			console.log('left-subtitle-click');
			this.arrowIndicatorMode = true;
			this.splitPoint = 0;
		};
		this.listenerFuncs['right-subtitle-click'] = () => {
			console.log('right-subtitle-click');
			this.arrowIndicatorMode = true;
			this.splitPoint = 1;
		};
		this.listenerFuncs['left-subtitle-focus'] = () => {
			console.log('left-subtitle-focus');
			this.arrowIndicatorMode = false;
			this.splitPoint = 0;
		};
		this.listenerFuncs['right-subtitle-focus'] = () => {
			console.log('right-subtitle-focus');
			this.arrowIndicatorMode = false;
			this.splitPoint = 1;
		};

		this.listenerFuncs['pointermove'] = (e: PointerEvent) => {
			if (this.mouseDown) {
				this.arrowIndicatorMode = true;
				this.updateSplitFromEvent(e);
			}
		};
		this.listenerFuncs['pointerup'] = (_e: PointerEvent) => {
			this.mouseDown = false;
			this.imagesContainer.style.cursor = 'default';
			this.dividerSurround.style.cursor = 'grab';
			this.dividerArrow.style.cursor = 'grab';
			this.componentEl.style.cursor = 'default';
		};
		this.listenerFuncs['pointerdown'] = (e: PointerEvent) => {
			this.arrowIndicatorMode = true;
			this.mouseDown = true;
			this.imagesContainer.style.cursor = 'grabbing';
			this.dividerSurround.style.cursor = 'grabbing';
			this.dividerArrow.style.cursor = 'grabbing';
			this.componentEl.style.cursor = 'grabbing';
			this.updateSplitFromEvent(e);
		};
	}

	protected override init(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'init');

		const headerHtml = this.htmlIfVar(Attrs.header, (text) => {
			return `<div class="${TAG_NAME}__header-container"><h5 class="${TAG_NAME}__header heading heading--05 animate">${text.toUpperCase()}</h5></div>`;
		});

		const subtitle = (text: string) => `<h5 class="${TAG_NAME}__subtitle-text animate heading heading--05">${text.toUpperCase()}</h5>`;

		const leftSubtitleHtml = this.htmlIfVar(Attrs.leftSubtitle, (text) => {
			return `<div class="${TAG_NAME}__subtitle-left">${subtitle(text)}</div>`;
		});
		const rightSubtitleHtml = this.htmlIfVar(Attrs.rightSubtitle, (text) => {
			return `<div class="${TAG_NAME}__subtitle-right">${subtitle(text)}</div>`;
		});

		const html: string = `
      <div class="${TAG_NAME}">
				<div class="container">
					<div class="grid">
						<div class="col-span-4 md:col-span-12 lg:col-start-3 lg:col-span-20">
							${headerHtml}
							<div class="${TAG_NAME}__images-container">
								<div class="${TAG_NAME}__images-container-inner">
									<div class="${TAG_NAME}__left-image-container">${this.imageHtml(Attrs.leftSrc, Attrs.leftSrcset, Attrs.leftSizes, Attrs.leftAlt)}</div>
									<div class="${TAG_NAME}__right-image-container">${this.imageHtml(Attrs.rightSrc, Attrs.rightSrcset, Attrs.rightSizes, Attrs.rightAlt)}</div>
									<div class="${TAG_NAME}__images-divider"></div>
									<div class="${TAG_NAME}__images-divider-surround"></div>
								</div>
								<div class="${TAG_NAME}__images-divider-arrow"></div>
							</div>
						</div>
					</div>
					<div class="grid ${TAG_NAME}__subtitles-container">
						<div class="col-span-1 md:col-span-3 lg:col-span-4 lg:col-start-3">
							${leftSubtitleHtml}
						</div>
						<div class="col-span-1 md:col-span-3 lg:col-span-4 col-start-4 md:col-start-10 lg:col-start-19">
							${rightSubtitleHtml}
						</div>
					</div>
				</div>
			</div>
    `;

		this.setInnerHtml('', '', html);

		this.leftSubtitle = this.element(`.${TAG_NAME}__subtitle-left`);
		this.rightSubtitle = this.element(`.${TAG_NAME}__subtitle-right`);

		if (this.leftSubtitle) {
			this.leftSubtitle.setAttribute('role', 'button');
			this.leftSubtitle.setAttribute('tabindex', '0');
			this.leftSubtitle.setAttribute('aria-label', 'Show left image');
			this.leftSubtitle.addEventListener('focus', this.listenerFuncs['left-subtitle-focus']);
			this.leftSubtitle.addEventListener('click', this.listenerFuncs['left-subtitle-click']);
		}
		if (this.rightSubtitle) {
			this.rightSubtitle.setAttribute('role', 'button');
			this.rightSubtitle.setAttribute('tabindex', '0');
			this.rightSubtitle.setAttribute('aria-label', 'Show right image');
			this.rightSubtitle.addEventListener('focus', this.listenerFuncs['right-subtitle-focus']);
			this.rightSubtitle.addEventListener('click', this.listenerFuncs['right-subtitle-click']);
		}

		this.imagesContainer = this.element(`.${TAG_NAME}__images-container`);
		this.componentEl = this.element(`.${TAG_NAME}`);
		this.divider = this.element(`.${TAG_NAME}__images-divider`);
		this.dividerSurround = this.element(`.${TAG_NAME}__images-divider-surround`);
		this.dividerArrow = this.element(`.${TAG_NAME}__images-divider-arrow`);
		this.leftImageContainer = this.element(`.${TAG_NAME}__left-image-container`);
		this.rightImageContainer = this.element(`.${TAG_NAME}__right-image-container`);

		if (this.vars.get<boolean>(Attrs.rosAnimation)) {
			gsap.set(this.leftImageContainer, { opacity: 0, y: '5%' });
			gsap.set(this.rightImageContainer, { opacity: 0, y: '5%' });
			this.imagesContainerTween?.kill();

			requestAnimationFrame(() => {
				this.imagesContainerTween = rosAnimation(this.imagesContainer, STAnimationType.triggered, {
					start: '30%',
					end: '100%',
					onStart: () => {
						gsap.to(this.leftImageContainer, {
							opacity: 1,
							duration: 0.8,
							y: '0%',
							ease: 'power2.out',
						});
						gsap.to(this.rightImageContainer, {
							opacity: 1,
							duration: 0.8,
							y: '0%',
							delay: 0.3,
							ease: 'power2.out',
						});
					},
				});
			});
		}

		this.imagesContainer.addEventListener('pointerdown', this.listenerFuncs['pointerdown']);
		window.addEventListener('pointerup', this.listenerFuncs['pointerup']);
		window.addEventListener('pointermove', this.listenerFuncs['pointermove']);

		this.dividerArrow.addEventListener('pointerdown', this.listenerFuncs['pointerdown']);

		this._updateSplit(this._splitPoint);
		this.observeSlotChanges(true);
	}

	private set arrowIndicatorMode(enabled: boolean) {
		if (this.dividerArrow) this.dividerArrow.style.display = enabled ? 'block' : 'none';
		if (!enabled) {
			if (this.leftSubtitle) this.leftSubtitle.style.opacity = '1';
			if (this.rightSubtitle) this.rightSubtitle.style.opacity = '1';
		}
	}

	private get arrowIndicatorMode(): boolean {
		return this.dividerArrow && this.dividerArrow.style.display !== 'none';
	}

	private imageHtml(srcKey: string, srcsetKey: string, sizesKey: string, altKey: string): string {
		const src = this.vars.get<string>(srcKey);
		const srcset = this.vars.get<string>(srcsetKey);
		const sizes = this.vars.get<string>(sizesKey);
		const alt = this.vars.get<string>(altKey);
		const classAttr: string = `class="${TAG_NAME}__images-container__image"`;
		let styleAttr: string = `style="--image-aspect-ratio: 16/9;"`;
		if (srcset?.length && sizes?.length) {
			return `<${HHDSImageTagName} ${classAttr} srcset="${srcset}" sizes="${sizes}" alt="${alt}" ${styleAttr}></${HHDSImageTagName}>`;
		} else if (src?.length) {
			return `<${HHDSImageTagName} ${classAttr} src="${src}" alt="${alt}" ${styleAttr}></${HHDSImageTagName}>`;
		}
		return '';
	}

	private updateSplitFromEvent(e: PointerEvent): void {
		if (!this.imagesContainer) return;
		const rect = this.imagesContainer.getBoundingClientRect();
		const offsetX = e.clientX - rect.left;
		let pos = 1 - offsetX / this.imagesContainer.clientWidth;
		pos = Math.max(0, Math.min(1, pos));
		this.splitPoint = pos;
	}

	set splitPoint(value: number) {
		const difference = Math.abs(this._splitPoint - value);
		const duration = difference < 0.2 ? 0.2 : 0.4;
		this.tween?.kill();
		this.tween = gsap.to(this, {
			_splitPoint: value,
			duration: duration,
			ease: 'power2.out',
			onUpdate: () => this._updateSplit(this._splitPoint),
		});
	}

	get splitPoint(): number {
		return this._splitPoint;
	}

	private _updateSplit(progress: number): void {
		const progressPc = Math.max(0, Math.min(1, progress)) * 100;
		const leftProgress = progressPc;
		const rightProgress = 100 - progressPc;

		this.leftImageContainer.style.setProperty('--image-clip-path', `inset(0 ${leftProgress}% 0 0)`);
		this.rightImageContainer.style.setProperty('--image-clip-path', `inset(0 0 0 ${rightProgress}%)`);

		this.divider?.style.setProperty('--split-image-divider-left', `${100 - progressPc}%`);
		this.dividerSurround?.style.setProperty('--split-image-divider-left', `${100 - progressPc}%`);
		this.dividerArrow?.style.setProperty('--split-image-divider-left', `${100 - progressPc}%`);

		const dividerRect = this.dividerArrow?.getBoundingClientRect();
		if (!dividerRect) return;

		const dividerCenterX = dividerRect ? (dividerRect.left + dividerRect.right) / 2 : 0;
		const fadeDistancePx: number = 40;
		const edgeMarginPx: number = 0;

		if (this.leftSubtitle && this.arrowIndicatorMode) {
			const leftSubtitleRect = this.leftSubtitle?.getBoundingClientRect();
			const leftSubtitleDistance = dividerCenterX - (leftSubtitleRect.right + edgeMarginPx);
			const leftSubtitleOpacity = Math.max(0, Math.min(1, leftSubtitleDistance / fadeDistancePx));
			this.leftSubtitle.style.opacity = leftSubtitleOpacity.toString();
		}
		if (this.rightSubtitle && this.arrowIndicatorMode) {
			const rightSubtitleRect = this.rightSubtitle?.getBoundingClientRect();
			const rightSubtitleDistance = dividerCenterX - (rightSubtitleRect.left - edgeMarginPx);
			const rightSubtitleOpacity = Math.max(0, Math.min(1, -rightSubtitleDistance / fadeDistancePx));
			this.rightSubtitle.style.opacity = rightSubtitleOpacity.toString();
		}
	}

	protected override destroy(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'destroy');
		this.leftSubtitle?.removeEventListener('click', this.listenerFuncs['left-subtitle-click']);
		this.rightSubtitle?.removeEventListener('click', this.listenerFuncs['right-subtitle-click']);
		this.leftSubtitle?.removeEventListener('focus', this.listenerFuncs['left-subtitle-focus']);
		this.rightSubtitle?.removeEventListener('focus', this.listenerFuncs['right-subtitle-focus']);
		this.imagesContainer?.removeEventListener('pointerdown', this.listenerFuncs['pointerdown']);
		window.removeEventListener('pointermove', this.listenerFuncs['pointermove']);
		window.removeEventListener('pointerup', this.listenerFuncs['pointerup']);
		this.tween?.kill();
		this.imagesContainerTween?.kill();
	}

	override onAttributeChanged(name: string, _oldValue: string, _newValue: string): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onAttributeChanged: ${name}`);
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, _elements: HTMLElement[]): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onSlotChange`);
	}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.header]: {
		description: 'Header text.',
		defaultValue: '',
		type: String,
	},
	[Attrs.leftSrc]: {
		description: 'URL for the left image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.leftSrcset]: {
		description: 'Srcset for the left image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.leftSizes]: {
		description: 'Sizes for the left image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.leftAlt]: {
		description: 'Alt text for the left image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.leftSubtitle]: {
		description: 'Subtitle for the left image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.rightSrc]: {
		description: 'URL for the right image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.rightSrcset]: {
		description: 'Srcset for the right image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.rightSizes]: {
		description: 'Sizes for the right image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.rightAlt]: {
		description: 'Alt text for the right image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.rightSubtitle]: {
		description: 'Subtitle for the right image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.rosAnimation]: {
		description: 'Enable scroll-triggered animations.',
		defaultValue: true,
		type: Boolean,
	},
};
